import { Stack } from "@mui/material";
import { TechnicalConfigTabHeader } from "features/shared/components/technical-config-tab/TechnicalConfigTabHeader";
import { getTechnicalConfigToastError } from "features/shared/lib/technical-config-tab/tableHeaders";
import { useActiveScenarioName } from "features/technical-data/scenario/hooks/useActiveScenarioName";
import { useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";

import type {
  RoomConfigCreatePayload,
  RoomConfigSavePayload,
  RoomTypeGroup,
  RoomTypeParentGroup,
} from "../model/types";

import { ROOM_CONFIG_CACHE_KEYS } from "../api/cacheKeys";
import { fetchRoomTypeDetail } from "../api/callers";
import {
  type BulkRoomConfigOperation,
  useBulkRoomConfig,
  useDeleteRoomConfig,
  useGetRoomTypeCatalog,
  useGetRoomTypeGroups,
  useSaveRoomConfig,
} from "../api/hooks";
import { mapDetailConfigToSavePayload } from "../lib/roomConfigForm";
import { type RoomBulkActionKind, RoomBulkActionModal } from "./RoomBulkActionModal";
import { RoomCatalogDrawer } from "./RoomCatalogDrawer";
import { RoomConfigDrawer } from "./RoomConfigDrawer";
import { RoomGroupTable } from "./RoomGroupTable";

type PendingBulkAction = {
  action: RoomBulkActionKind;
  afterSuccess?: () => void;
  count: number;
  resolveOperations: () => Promise<BulkRoomConfigOperation[]>;
  successMessageKey: string;
};

const UNASSIGNED_GROUP_ID = "__unassigned__";

export const RoomTab = ({ projectId }: { projectId: string }) => {
  const scenario = useActiveScenarioName();
  const queryClient = useQueryClient();
  const { data: groups = [], isLoading, refetch } = useGetRoomTypeGroups(projectId);
  const { data: catalog = [] } = useGetRoomTypeCatalog(Boolean(projectId));
  const { mutate: deleteConfig } = useDeleteRoomConfig();
  const { mutate: saveConfig } = useSaveRoomConfig();
  const { isLoading: isBulkProcessing, mutateAsync: runBulkAction } = useBulkRoomConfig();
  const [selectedRoomFunctionValue, setSelectedRoomFunctionValue] = useState<null | string>(null);
  const [selectedParent, setSelectedParent] = useState<null | RoomTypeParentGroup>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [pendingBulkAction, setPendingBulkAction] = useState<null | PendingBulkAction>(null);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [isBulkFinishing, setIsBulkFinishing] = useState(false);
  const [tableVersion, setTableVersion] = useState(0);
  const selectedGroup = groups.find((group) => group.roomFunctionValue === selectedRoomFunctionValue) ?? null;
  const groupedRows = useMemo<RoomTypeParentGroup[]>(() => {
    const grouped = new Map<string, RoomTypeGroup[]>();

    groups.forEach((group) => {
      const key = group.roomTypeName?.trim() || UNASSIGNED_GROUP_ID;
      grouped.set(key, [...(grouped.get(key) ?? []), group]);
    });

    return [...grouped.entries()]
      .map(([key, children]) => {
        const colors = new Set(children.map((child) => child.color).filter(Boolean));
        return {
          children,
          color: colors.size === 1 ? [...colors][0] : undefined,
          groupId: key,
          hasConfig: children.every((child) => child.hasConfig),
          roomFunctionCount: children.length,
          roomTypeGroupName: key === UNASSIGNED_GROUP_ID ? "Unassigned" : key,
          roomTypeName: key === UNASSIGNED_GROUP_ID ? undefined : key,
        };
      })
      .sort((a, b) => {
        if (a.groupId === UNASSIGNED_GROUP_ID) return -1;
        if (b.groupId === UNASSIGNED_GROUP_ID) return 1;
        return a.roomTypeGroupName.localeCompare(b.roomTypeGroupName);
      });
  }, [groups]);

  const handleDelete = (group: RoomTypeGroup) => {
    if (!group.hasConfig) return;
    deleteConfig(
      { projectId, roomFunctionValue: group.roomFunctionValue },
      { onSuccess: () => setSelectedRoomFunctionValue(null) }
    );
  };

  const getCreatePayload = (roomTypeName: string): RoomConfigCreatePayload => {
    const catalogItem = catalog.find((item) => item.roomTypeName === roomTypeName);

    if (catalogItem) return { roomTypeDbId: catalogItem.id };
    return { saveAsRoomTypeName: roomTypeName };
  };

  const getPatchPayload = (roomTypeName: string): RoomConfigSavePayload => {
    const catalogItem = catalog.find((item) => item.roomTypeName === roomTypeName);

    if (catalogItem) return { roomTypeDbId: catalogItem.id };
    return { roomTypeName };
  };

  const getColorPayload = (value: string): RoomConfigSavePayload => {
    return { color: value };
  };

  const loadTemplatePayload = async (child: RoomTypeGroup) => {
    const detail = await queryClient.fetchQuery(
      ROOM_CONFIG_CACHE_KEYS.detail(projectId, child.roomFunctionValue, scenario),
      () => fetchRoomTypeDetail({ projectId, roomFunctionValue: child.roomFunctionValue, scenario })
    );

    if (!detail?.config) return null;
    return mapDetailConfigToSavePayload(detail.config, child.color ?? detail.config.color);
  };

  const buildRoomTypeOperations = async (
    newcomers: RoomTypeGroup[],
    targetName: string,
    templateChild?: RoomTypeGroup
  ): Promise<BulkRoomConfigOperation[]> => {
    const template = templateChild ? await loadTemplatePayload(templateChild) : null;

    return newcomers.map((group) => {
      const roomTypePayload = group.hasConfig ? getPatchPayload(targetName) : getCreatePayload(targetName);

      if (!template) {
        return {
          data: roomTypePayload,
          hasConfig: group.hasConfig,
          kind: "save" as const,
          roomFunctionValue: group.roomFunctionValue,
        };
      }

      const alignedPayload = { ...template, ...getPatchPayload(targetName) };

      if (group.hasConfig) {
        return {
          data: alignedPayload,
          hasConfig: true as const,
          kind: "save" as const,
          roomFunctionValue: group.roomFunctionValue,
        };
      }

      return {
        data: roomTypePayload,
        followUpData: alignedPayload,
        hasConfig: false as const,
        kind: "save" as const,
        roomFunctionValue: group.roomFunctionValue,
      };
    });
  };

  const openBulkAction = (action: PendingBulkAction) => {
    setBulkProgress(0);
    setPendingBulkAction(action);
  };

  const handleRoomTypeChange = (newcomers: RoomTypeGroup[], nextRoomTypeName: string) => {
    const targetName = nextRoomTypeName.trim();
    if (!targetName || newcomers.length === 0) return;
    if (newcomers.every((child) => (child.roomTypeName?.trim() ?? "") === targetName)) return;

    const newcomerIds = new Set(newcomers.map((child) => child.roomFunctionValue));
    const targetParent = groupedRows.find((row) => row.groupId === targetName);
    const templateChild = targetParent?.children.find((child) => !newcomerIds.has(child.roomFunctionValue));

    openBulkAction({
      action: templateChild?.hasConfig ? "alignRoomType" : "assignRoomType",
      count: newcomers.length,
      resolveOperations: () => buildRoomTypeOperations(newcomers, targetName, templateChild),
      successMessageKey: "TechnicalData.room.saveSuccess",
    });
  };

  const handleBlur = (group: RoomTypeGroup, fieldName: string, value: string) => {
    if (!["color", "roomTypeName"].includes(fieldName) || !value) return;

    if (fieldName === "roomTypeName") {
      handleRoomTypeChange([group], value);
      return;
    }

    saveConfig({
      data: getColorPayload(value),
      hasConfig: group.hasConfig,
      projectId,
      roomFunctionValue: group.roomFunctionValue,
    });
  };

  const handleParentBlur = (parent: RoomTypeParentGroup, fieldName: string, value: string) => {
    if (!["color", "roomTypeName"].includes(fieldName) || !value) return;

    if (fieldName === "roomTypeName") {
      handleRoomTypeChange(parent.children, value);
      return;
    }

    openBulkAction({
      action: "changeColor",
      count: parent.children.length,
      resolveOperations: async () =>
        parent.children.map((group) => ({
          data: getColorPayload(value),
          hasConfig: group.hasConfig,
          kind: "save" as const,
          roomFunctionValue: group.roomFunctionValue,
        })),
      successMessageKey: "TechnicalData.room.saveSuccess",
    });
  };

  const handleParentDelete = (parent: RoomTypeParentGroup) => {
    const operations: BulkRoomConfigOperation[] = parent.children
      .filter((group) => group.hasConfig)
      .map((group) => ({ kind: "delete", roomFunctionValue: group.roomFunctionValue }));

    openBulkAction({
      action: "deleteConfig",
      count: operations.length,
      resolveOperations: async () => operations,
      successMessageKey: "TechnicalData.room.deleteSuccess",
    });
  };

  const closeBulkModal = () => {
    if (isBulkProcessing || isBulkFinishing) return;
    setPendingBulkAction(null);
    setTableVersion((version) => version + 1);
  };

  const confirmBulkAction = async () => {
    if (!pendingBulkAction) return;

    setIsBulkFinishing(true);
    let mutationStarted = false;
    try {
      const operations = await pendingBulkAction.resolveOperations();
      if (operations.length === 0) return;

      mutationStarted = true;
      await runBulkAction({
        onProgress: (completed, total) => setBulkProgress((completed / total) * 100),
        operations,
        projectId,
        successMessageKey: pendingBulkAction.successMessageKey,
      });
      await refetch();
      pendingBulkAction.afterSuccess?.();
      setPendingBulkAction(null);
      setTableVersion((version) => version + 1);
    } catch (error) {
      if (!mutationStarted) {
        toast.error(getTechnicalConfigToastError(error as { response?: { data?: unknown } }));
      }
      // The bulk mutation reports its own API error and keeps the dialog open for retry.
    } finally {
      setIsBulkFinishing(false);
    }
  };

  return (
    <Stack className="w-full flex-col items-stretch gap-6">
      <TechnicalConfigTabHeader
        descriptionKey="TechnicalData.room.description"
        manageCatalogKey="TechnicalData.room.manageRoomTypes"
        onManageCatalog={() => setCatalogOpen(true)}
        titleKey="TechnicalData.room.title"
      />
      <RoomGroupTable
        catalog={catalog}
        groups={groupedRows}
        isLoading={isLoading}
        key={tableVersion}
        onChangeBlur={handleBlur}
        onConfigure={(group) => setSelectedRoomFunctionValue(group.roomFunctionValue)}
        onDelete={handleDelete}
        onParentChangeBlur={handleParentBlur}
        onParentConfigure={(parent) => {
          const representative = parent.children.find((child) => child.hasConfig) ?? parent.children[0];
          setSelectedParent(parent);
          setSelectedRoomFunctionValue(representative?.roomFunctionValue ?? null);
        }}
        onParentDelete={handleParentDelete}
      />
      <RoomConfigDrawer
        bulkGroups={selectedParent?.children}
        group={selectedGroup}
        onClose={() => {
          setSelectedParent(null);
          setSelectedRoomFunctionValue(null);
        }}
        open={Boolean(selectedRoomFunctionValue)}
        projectId={projectId}
      />
      <RoomCatalogDrawer onClose={() => setCatalogOpen(false)} open={catalogOpen} />
      <RoomBulkActionModal
        action={pendingBulkAction?.action ?? "assignRoomType"}
        count={pendingBulkAction?.count ?? 0}
        isProcessing={isBulkProcessing || isBulkFinishing}
        onCancel={closeBulkModal}
        onConfirm={() => void confirmBulkAction()}
        open={Boolean(pendingBulkAction)}
        progress={bulkProgress}
      />
    </Stack>
  );
};
